import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clearTokens } from '../lib/api-client';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Confirmation page shown after account deletion.
 * Clears auth tokens as a defensive measure (in case user landed here
 * from GDPR email link without being logged in).
 */
export default function AccountDeleted() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const success = searchParams.get('success');
  const { t } = useTranslation();

  useEffect(() => {
    // Defensive: ensure no residual tokens
    clearTokens();
  }, []);

  if (error) {
    const messages: Record<string, string> = {
      missing_token: t('accountDeleted.errorMissingToken'),
      invalid_token: t('accountDeleted.errorInvalidToken'),
    };
    const msg = messages[error] || t('accountDeleted.errorGeneric');
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-text-primary mb-2">
            {t('accountDeleted.errorTitle')}
          </h1>
          <p className="text-text-secondary mb-6">{msg}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            {t('accountDeleted.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">✓</div>
        <h1 className="text-xl font-bold text-text-primary mb-2">
          {t('accountDeleted.title')}
        </h1>
        <p className="text-text-secondary mb-2">
          {t('accountDeleted.description')}
        </p>
        <p className="text-text-secondary text-sm mb-6">
          {success
            ? t('accountDeleted.successMessage')
            : t('accountDeleted.processedMessage')}
        </p>
        <Link
          to="/"
          className="inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
        >
          {t('accountDeleted.backToHome')}
        </Link>
      </div>
    </div>
  );
}
